import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import dvdlocator from "../../../../apis/dvdlocator";
import { getCurrentUserId } from "../../../../auth/currentUser";
import { Freshness } from "./SupplierRollupCell";

/**
 * Shared pieces of the metric provenance UI: every computed product metric
 * (MRCOG, MAVCG, ATS / MAI / MONO, restock status) renders a cell that opens
 * a detail popover listing the rows the value was computed from, as they
 * were at calculation time and as they are now, with a link to those rows in
 * the Inventory grid and a "Recalculate" action that queues the product for
 * the inventory_metrics service.
 */

export const FRESHNESS_COLORS: Record<Freshness, string> = {
  fresh: "#339933",
  aging: "#E6A700",
  stale: "#CC0000",
  never: "#B0B0B0",
};

export const money = (v: number | null | undefined) =>
  v === null || v === undefined
    ? "—"
    : Number(v).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

export const quantity = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : String(v);

export const FreshnessDot = ({ freshness }: { freshness: Freshness }) => (
  <Box
    component="span"
    sx={{
      width: 8,
      height: 8,
      borderRadius: "50%",
      display: "inline-block",
      backgroundColor: FRESHNESS_COLORS[freshness],
      mr: 0.5,
    }}
  />
);

/** A changed-since-calculation value: red when it drifted, "gone" when the row is no longer there. */
export function DriftCell({
  then,
  now,
  format = quantity,
  align = "right",
}: {
  then: number | string | null;
  now: number | string | null;
  format?: (v: any) => string;
  align?: "left" | "right";
}) {
  const drifted = valueDrifted(then, now);
  return (
    <span
      style={
        drifted
          ? { color: "#CC0000", fontWeight: 700, textAlign: align }
          : undefined
      }
    >
      {now === null || now === undefined
        ? "gone"
        : drifted
        ? format(now)
        : "same"}
    </span>
  );
}

export function valueDrifted(
  then: number | string | null | undefined,
  now: number | string | null | undefined
): boolean {
  if (now === null || now === undefined) return false;
  if (then === null || then === undefined) return true;
  if (typeof then === "number" || typeof now === "number") {
    return Math.abs(Number(now) - Number(then)) >= 0.005;
  }
  return String(then) !== String(now);
}

export async function queueProductRecalc(productId: number): Promise<void> {
  await dvdlocator.post(`/product/${productId}/recalculate`, {
    userId: getCurrentUserId(),
  });
}

/** Inventory grid, pre-filtered to the given supply UPCs, for a new tab. */
export function inventoryGridUrlForUpcs(upcs: string[]): string {
  const params = new URLSearchParams();
  params.set("tab", "inventory");
  params.set("inventory_filter_supply.upc_operator", "isAnyOf");
  params.set("inventory_filter_supply.upc_value", JSON.stringify(upcs));
  return `/datatables/pricetool?${params.toString()}`;
}

/** Inventory grid, pre-filtered to the given inventory ids (every UPC of each), for a new tab. */
export function inventoryGridUrlForInventoryIds(ids: number[]): string {
  const params = new URLSearchParams();
  params.set("tab", "inventory");
  params.set("inventory_filter_supply.inventory_id_operator", "isAnyOf");
  params.set(
    "inventory_filter_supply.inventory_id_value",
    JSON.stringify(ids.map(String))
  );
  return `/datatables/pricetool?${params.toString()}`;
}

export function InventoryGridLink({
  href,
  count,
  noun = "component",
}: {
  href: string;
  count: number;
  noun?: string;
}) {
  if (count === 0) return null;
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener"
      variant="body2"
      sx={{ display: "inline-block", mt: 0.5 }}
    >
      Open {count === 1 ? "this" : `these ${count}`} {noun}
      {count === 1 ? "" : "s"} in the Inventory grid ↗
    </Link>
  );
}

/** Loads a detail document once per product id. */
export function useProvenanceDetail<T>(
  productId: number,
  fetcher: (productId: number) => Promise<T>,
  what: string
) {
  const [detail, setDetail] = useState<T | null>(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setError("");
    try {
      setDetail(await fetcher(productId));
    } catch (err: any) {
      setError(`Could not load ${what}: ${err?.message ?? err}`);
    }
  }, [productId, fetcher, what]);
  useEffect(() => {
    load();
  }, [load]);
  return { detail, error };
}

/** The common frame of a detail: title, loading / error state, footer with Recalculate and Close. */
export function ProvenanceFrame({
  title,
  productTitle,
  productId,
  error,
  loaded,
  recalcLabel,
  onClose,
  children,
}: {
  title: string;
  productTitle?: string;
  productId: number;
  error: string;
  loaded: boolean;
  recalcLabel: string;
  onClose?: () => void;
  children?: React.ReactNode;
}) {
  const [queued, setQueued] = useState<"idle" | "sending" | "done" | "failed">(
    "idle"
  );
  const recalc = async () => {
    setQueued("sending");
    try {
      await queueProductRecalc(productId);
      setQueued("done");
    } catch {
      setQueued("failed");
    }
  };
  return (
    <Box sx={{ minWidth: 420 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {title}
        {productTitle ? ` · ${productTitle}` : ""}
      </Typography>
      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}
      {!loaded && !error && <CircularProgress size={20} />}
      {loaded && children}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: "flex-end",
          alignItems: "center",
          mt: 1,
        }}
      >
        {queued === "done" && (
          <Typography variant="caption" color="text.secondary">
            Queued; every metric of this product refreshes within about a
            minute.
          </Typography>
        )}
        {queued === "failed" && (
          <Typography variant="caption" color="error">
            Could not queue the recalculation.
          </Typography>
        )}
        <Button
          size="small"
          onClick={recalc}
          disabled={queued === "sending" || queued === "done"}
        >
          {recalcLabel}
        </Button>
        {onClose && (
          <Button size="small" variant="contained" onClick={onClose}>
            Close
          </Button>
        )}
      </Box>
    </Box>
  );
}

/**
 * A clickable cell body that opens its detail in a popover. The popover is a
 * portal, so its events are stopped before they reach the grid cell (which
 * would otherwise start editing or change the selection).
 */
export function ProvenanceCell({
  tooltip,
  children,
  renderDetail,
  maxWidth = 760,
}: {
  /** Plain text; newlines are kept. */
  tooltip: string;
  children: React.ReactNode;
  renderDetail: (close: () => void) => React.ReactNode;
  maxWidth?: number;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const close = () => setAnchorEl(null);
  return (
    <>
      <Tooltip
        title={<span style={{ whiteSpace: "pre-line" }}>{tooltip}</span>}
        arrow
      >
        <Box
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1.25,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {children}
        </Box>
      </Tooltip>
      {anchorEl && (
        <Popover
          open
          anchorEl={anchorEl}
          onClose={close}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <Box sx={{ p: 2, maxWidth }}>{renderDetail(close)}</Box>
        </Popover>
      )}
    </>
  );
}

/** Dialog wrapper used by the context menus. */
export function ProvenanceDialog({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>{children}</DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
